/**
 * ApiClient module
 * Provides functions for making HTTP requests to the API with proper authentication and telemetry
 */

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpStatusCode, duration, requestStartTime } from '../common/constants';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';


import { TelemetryClient } from '../telemetry/TelemetryClient';
import { getAccessToken } from '../services/AuthClient';
import { TelemetryConfig } from '../telemetry/TelemetryConfig';
import { appConfiguration } from '../../environments/app.config';



/**
 * Creates and configures an Axios instance with authentication, retry logic, and telemetry
 * @returns Configured Axios instance
 */
export const apiClient = (): AxiosInstance => {
  // Ensure telemetry is initialized so the SDK's automatic dependency correlation is active.
  TelemetryClient.getInstance(TelemetryConfig);

  // Create Axios instance with base URL
  const axiosInstance = axios.create({ baseURL: appConfiguration.api.baseUrl });

  // Request interceptor for adding headers and timing information
  axiosInstance.interceptors.request.use(
    async (axiosConfig) => {
      // Get authentication token
      // const token = await getAccessToken();
      // Set headers with content type using Axios's setters
      // Create default headers if needed
      if (!axiosConfig.headers) {
        // Use Axios's standard header creation
        axiosConfig.headers = new axios.AxiosHeaders();
      }
      
      // Use axios header methods to set values safely
      const headers = axiosConfig.headers;
      const token = await getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
      
      // Only set Content-Type to application/json if not FormData
      // FormData should let the browser set Content-Type with boundary
      if (!(axiosConfig.data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      headers[requestStartTime] = new Date().getTime().toString();
      
      return axiosConfig;
    },
    (error: any) => {
      return Promise.reject(error);
    },
  );

  /**
   * Retry configuration
   */
  const retryConfig: {
    maxAttempts: number;
    retryCount: number;
    waitTime: number;
  } = {
    maxAttempts: 3, // Maximum number of retry attempts
    retryCount: 0, // Current retry count
    waitTime: 5000, // Wait time between retries in milliseconds
  };

  /**
   * Determines if a request should be retried based on the error status code
   * @param error - The error from the failed request
   * @returns Boolean indicating if the request should be retried
   */
  const shouldRetry = (error: any): boolean => {
    const { maxAttempts, retryCount } = retryConfig;
    // Retry on server errors and certain status codes
    if (
      retryCount < maxAttempts &&
      (error?.response.status === HttpStatusCode.ServiceUnavailable ||
        error?.response.status === HttpStatusCode.RequestThrottling ||
        error?.response.status === HttpStatusCode.RequestTimeOut ||
        (error?.response.status >= 500 && error?.response.status <= 599))
    ) {
      return true;
    }
    return false;
  };

  /**
   * Response interceptor for handling successful responses and errors with retry logic
   */
  axiosInstance.interceptors.response.use(
    // Success handler
    (response: AxiosResponse): AxiosResponse => {
      // Calculate request duration
      response.config.headers[duration] = new Date().getTime() - Number(response.config.headers[requestStartTime]);
      // HTTP dependencies are auto-collected by the Application Insights SDK with real
      // W3C trace context; tracking them again here would duplicate telemetry and break
      // correlation with the API's operation_Id.
      return response;
    },
    // Error handler
    (error: any) => {
      // Handle network errors (no response)
      if (typeof error.response === 'undefined') {
        if (error.config) {
          const { maxAttempts, retryCount } = error.config;
          retryConfig.retryCount = retryCount + 1;
          error.config.__isRetryRequest = true;
          const { waitTime } = retryConfig;
          
          // Retry with delay if we haven't exceeded max attempts
          if (retryConfig.retryCount < maxAttempts && waitTime > 0) {
            return new Promise<AxiosResponse>((resolve) => {
              setTimeout(
                () => resolve(axiosInstance.request(error.config)),
                waitTime,
              );
            });
          }
        }
        return Promise.reject(error);
      }
      
      // Handle server errors with retry logic
      if (error?.config && shouldRetry(error)) {
        const { retryCount } = error.config;
        retryConfig.retryCount = retryCount + 1;
        error.config.__isRetryRequest = true;
        const { waitTime } = retryConfig;
        
        // Retry with delay
        if (waitTime > 0) {
          return new Promise<AxiosResponse>((resolve) => {
            setTimeout(
              () => resolve(axiosInstance.request(error.config)),
              waitTime,
            );
          });
        }
        
        // Immediate retry
        return axiosInstance.request(error.config);
      }

      // Calculate request duration for failed requests
      error.config.headers[duration] = new Date().getTime() - Number(error.config.headers[requestStartTime]);
      // Failed dependencies are auto-collected by the Application Insights SDK.
      return Promise.reject(error);
    },
  );

  return axiosInstance;
};

/**
 * Performs a GET request
 * @param url - The URL to send the request to (without the base URL)
 * @param config - Optional Axios request configuration
 * @returns Promise with the response data
 */
export const get = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return apiClient().get<T>(url, config);
};

/**
 * Performs a POST request
 * @param url - The URL to send the request to (without the base URL)
 * @param payload - The data to send in the request body
 * @param config - Optional Axios request configuration
 * @returns Promise with the response data
 */
export const post = <T>(
  url: string,
  payload: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return apiClient().post<T>(url, payload, config);
};

/**
 * Performs a PUT request
 * @param url - The URL to send the request to (without the base URL)
 * @param payload - The data to send in the request body
 * @param config - Optional Axios request configuration
 * @returns Promise with the response data
 */
export const put = <T>(
  url: string,
  payload: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return apiClient().put<T>(url, payload, config);
};

/**
 * Performs a DELETE request
 * @param url - The URL to send the request to (without the base URL)
 * @param config - Optional Axios request configuration
 * @returns Promise with the response data
 */
export const del = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return apiClient().delete<T>(url, config);
};

/**
 * Uploads a file using multipart/form-data
 * @param url - The URL to send the request to (without the base URL)
 * @param file - The File object to upload
 * @param additionalFields - Optional additional form fields to include in the upload
 * @param config - Optional Axios request configuration
 * @returns Promise with the response data
 */
export const uploadFile = <T>(
  url: string,
  file: File,
  additionalFields?: Record<string, string>,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  const formData = new FormData();
  // API expects 'File' with capital F
  formData.append('File', file);
  
  // Add any additional fields to the form data
  if (additionalFields) {
    Object.entries(additionalFields).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  // The interceptor will detect FormData and skip setting Content-Type
  // allowing the browser to set it with proper multipart boundary
  return apiClient().post<T>(url, formData, config);
};

/**
 * Gets a cancellation token for aborting requests
 * @returns Cancellation token source
 */
export const getCancellationToken = () => {
  return new AbortController();
};
